import { useEffect, useMemo, useState } from "react";
import {
  AppWindow, Plus, PencilSimple, Trash, X, FloppyDisk,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

/**
 * Applications Panel — Version 2.0 Application Registry admin surface.
 *
 * Administrator/Owner-only. Full CRUD over the catalogue that powers the
 * "My Applications" dashboard widget. Visibility is a strict allow-list:
 * only users whose email appears in `permitted_emails` (or whose role appears
 * in `permitted_roles`) see the application on their dashboard.
 *
 * DSG-001 compliant.
 */
export default function ApplicationsPanel() {
  const [apps, setApps] = useState([]);
  const [directory, setDirectory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | app object
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, d] = await Promise.all([
        api.get("/admin/applications"),
        api.get("/admin/applications/directory"),
      ]);
      setApps(a.data);
      setDirectory(d.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (app) => {
    try {
      await api.delete(`/admin/applications/${app.id}`);
      toast.success(`Removed ${app.name}`);
      setDeleteConfirm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete application");
    }
  };

  return (
    <div data-testid="admin-panel-applications">
      <div className="mb-5 flex items-start justify-between border-b border-slate-200 pb-3">
        <div>
          <h2 className="font-display text-lg font-semibold text-slate-900">Application Registry</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Catalogue every native module, existing desktop tool, and future web/mobile
            application. Users see only what they are explicitly granted.
          </p>
        </div>
        <Button
          data-testid="applications-new-btn"
          onClick={() => setEditing("new")}
          className="bg-charcoal hover:bg-charcoal"
        >
          <Plus size={14} weight="bold" className="mr-1" /> New Application
        </Button>
      </div>

      {loading && (
        <div className="py-8 text-center text-sm text-slate-500">Loading applications…</div>
      )}

      {!loading && apps.length === 0 && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-8 text-center">
          <AppWindow size={28} weight="light" className="mx-auto text-slate-400" />
          <div className="mt-2 text-sm font-medium text-slate-700">No applications registered yet</div>
          <div className="mt-1 text-xs text-slate-500">
            Register your first application to populate the "My Applications" widget.
          </div>
        </div>
      )}

      {!loading && apps.length > 0 && (
        <div className="overflow-hidden rounded-md border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2.5 font-semibold">Application</th>
                <th className="px-4 py-2.5 font-semibold">Type</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5 font-semibold">Deployment</th>
                <th className="px-4 py-2.5 font-semibold">Owner</th>
                <th className="px-4 py-2.5 font-semibold">Visibility</th>
                <th className="px-4 py-2.5 font-semibold w-24 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {apps.map((app) => (
                <tr key={app.id} className="border-t border-slate-100" data-testid={`application-row-${app.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-slate-900">{app.name}</div>
                      {app.is_host && (
                        <span
                          data-testid={`application-host-badge-${app.id}`}
                          className="inline-flex items-center gap-0.5 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0 text-[10px] font-semibold text-amber-800"
                          title="Host Application — drives Header, Sidebar, and Sign-in identity"
                        >
                          ⭐ Host
                        </span>
                      )}
                    </div>
                    {app.version && (
                      <div className="mt-0.5 font-mono text-[11px] text-slate-500">v{app.version}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{app.app_type}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] font-medium text-slate-700">
                      {app.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {app.deployment ? (
                      <span className="inline-flex items-center rounded-sm border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-700">
                        {app.deployment}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {app.owner_name || <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {app.permitted_emails.length} {app.permitted_emails.length === 1 ? "user" : "users"}
                    {app.permitted_roles.length > 0 && ` + ${app.permitted_roles.length} role${app.permitted_roles.length === 1 ? "" : "s"}`}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      data-testid={`application-edit-${app.id}`}
                      onClick={() => setEditing(app)}
                      className="mr-1 rounded-md p-1.5 text-slate-600 hover:bg-slate-100"
                      title="Edit"
                    >
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button
                      data-testid={`application-delete-${app.id}`}
                      onClick={() => setDeleteConfirm(app)}
                      className="rounded-md p-1.5 text-slate-600 hover:bg-red-50 hover:text-red-600"
                      title="Delete"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && directory && (
        <ApplicationEditor
          initial={editing === "new" ? null : editing}
          directory={directory}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {deleteConfirm && (
        <Dialog open onOpenChange={(o) => !o && setDeleteConfirm(null)}>
          <DialogContent data-testid="application-delete-confirm">
            <DialogHeader>
              <DialogTitle>Remove {deleteConfirm.name}?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">
              This unregisters the application from the platform. Users will no
              longer see it in "My Applications". You can re-register it later.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
              <Button
                data-testid="application-delete-confirm-btn"
                onClick={() => handleDelete(deleteConfirm)}
                className="bg-red-600 hover:bg-red-700"
              >
                Remove
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function ApplicationEditor({ initial, directory, onClose, onSaved }) {
  const isNew = !initial;
  const [form, setForm] = useState(() => ({
    name: initial?.name || "",
    description: initial?.description || "",
    app_type: initial?.app_type || directory.app_types[0],
    status: initial?.status || directory.statuses[0],
    deployment: initial?.deployment || (directory.deployments && directory.deployments[0]) || "Cloud",
    version: initial?.version || "",
    owner_email: initial?.owner_email || "",
    launch_method: initial?.launch_method || "none",
    launch_target: initial?.launch_target || "",
    icon: initial?.icon || "app-window",
    display_order: initial?.display_order ?? 100,
    permitted_emails: initial?.permitted_emails || [],
    permitted_roles: initial?.permitted_roles || [],
    // DSG-006 · Host application identity fields.
    subtitle: initial?.subtitle || "",
    department: initial?.department || "",
  }));
  const [saving, setSaving] = useState(false);
  const isHost = !!initial?.is_host;
  const [settingHost, setSettingHost] = useState(false);

  const usersByEmail = useMemo(() => {
    const m = new Map();
    directory.users.forEach((u) => m.set(u.email, u));
    return m;
  }, [directory.users]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleEmail = (email) => {
    setForm((f) => ({
      ...f,
      permitted_emails: f.permitted_emails.includes(email)
        ? f.permitted_emails.filter((e) => e !== email)
        : [...f.permitted_emails, email],
    }));
  };
  const toggleRole = (role) => {
    setForm((f) => ({
      ...f,
      permitted_roles: f.permitted_roles.includes(role)
        ? f.permitted_roles.filter((r) => r !== role)
        : [...f.permitted_roles, role],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const owner = form.owner_email ? usersByEmail.get(form.owner_email) : null;
      const payload = {
        ...form,
        version: form.version.trim() || null,
        owner_email: form.owner_email || null,
        owner_name: owner ? (owner.full_name || owner.email) : null,
        launch_target: form.launch_target.trim() || null,
        display_order: Number(form.display_order) || 100,
      };
      if (isNew) {
        await api.post("/admin/applications", payload);
        toast.success("Application registered");
      } else {
        await api.patch(`/admin/applications/${initial.id}`, payload);
        toast.success("Application updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save application");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="application-editor"
        className="max-h-[90vh] max-w-2xl overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle>{isNew ? "Register Application" : `Edit ${initial.name}`}</DialogTitle>
              {isHost && (
                <div
                  data-testid="app-editor-host-badge"
                  className="mt-1 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800"
                >
                  ⭐ Host Application
                </div>
              )}
            </div>
            {!isNew && !isHost && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid="app-editor-set-host"
                disabled={settingHost}
                onClick={async () => {
                  setSettingHost(true);
                  try {
                    await api.post(`/admin/applications/${initial.id}/set-host`);
                    toast.success(`${initial.name} is now the Host Application`);
                    onSaved();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Could not set host");
                  } finally {
                    setSettingHost(false);
                  }
                }}
              >
                ⭐ Set as Host Application
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FieldWrap label="Name" required full>
            <Input
              data-testid="app-editor-name"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="Bank Sheet Review"
            />
          </FieldWrap>
          <FieldWrap label="Description" full>
            <Textarea
              data-testid="app-editor-description"
              rows={3}
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              placeholder="Short summary shown on the user's dashboard card."
            />
          </FieldWrap>

          {/* DSG-006 · Host Application identity — visible on the host
              record (or when the admin is promoting a candidate).
              These fields drive Header / Sidebar / SignIn / Live Preview. */}
          {isHost && (
            <>
              <FieldWrap label="Subtitle">
                <Input
                  data-testid="app-editor-subtitle"
                  value={form.subtitle}
                  onChange={(e) => setField("subtitle", e.target.value)}
                  placeholder="Fuel Order Centre"
                />
              </FieldWrap>
              <FieldWrap label="Department / Business Unit">
                <Input
                  data-testid="app-editor-department"
                  value={form.department}
                  onChange={(e) => setField("department", e.target.value)}
                  placeholder="Operations"
                />
              </FieldWrap>
            </>
          )}

          <FieldWrap label="Type">
            <Select value={form.app_type} onValueChange={(v) => setField("app_type", v)}>
              <SelectTrigger data-testid="app-editor-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {directory.app_types.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldWrap>
          <FieldWrap label="Status">
            <Select value={form.status} onValueChange={(v) => setField("status", v)}>
              <SelectTrigger data-testid="app-editor-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {directory.statuses.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-[11px] text-slate-400">
              Business lifecycle — whether the application is available.
            </p>
          </FieldWrap>
          <FieldWrap label="Deployment">
            <Select value={form.deployment} onValueChange={(v) => setField("deployment", v)}>
              <SelectTrigger data-testid="app-editor-deployment"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(directory.deployments || ["Cloud", "Desktop", "Hybrid", "External"]).map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-[11px] text-slate-400">
              Hosting model — where the application runs.
            </p>
          </FieldWrap>

          <FieldWrap label="Version">
            <Input
              data-testid="app-editor-version"
              value={form.version}
              onChange={(e) => setField("version", e.target.value)}
              placeholder="e.g. 1.4.2"
            />
          </FieldWrap>
          <FieldWrap label="Display Order">
            <Input
              data-testid="app-editor-order"
              type="number"
              value={form.display_order}
              onChange={(e) => setField("display_order", e.target.value)}
            />
          </FieldWrap>

          <FieldWrap label="Owner">
            <Select
              value={form.owner_email || "__none__"}
              onValueChange={(v) => setField("owner_email", v === "__none__" ? "" : v)}
            >
              <SelectTrigger data-testid="app-editor-owner"><SelectValue placeholder="Choose owner" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {directory.users.map((u) => (
                  <SelectItem key={u.email} value={u.email}>
                    {u.full_name || u.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FieldWrap>
          <FieldWrap label="Icon">
            <Input
              data-testid="app-editor-icon"
              value={form.icon}
              onChange={(e) => setField("icon", e.target.value)}
              placeholder="bank | desktop | spreadsheet | web | app-window"
            />
          </FieldWrap>

          <FieldWrap label="Launch Method">
            <Select value={form.launch_method} onValueChange={(v) => setField("launch_method", v)}>
              <SelectTrigger data-testid="app-editor-launch-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {directory.launch_methods.map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-[11px] text-slate-400">
              Technical mechanism — how the application starts.
            </p>
          </FieldWrap>
          <FieldWrap label="Launch Target">
            <Input
              data-testid="app-editor-launch-target"
              value={form.launch_target}
              onChange={(e) => setField("launch_target", e.target.value)}
              placeholder="URL or route (blank if none)"
              disabled={form.launch_method === "none"}
            />
          </FieldWrap>

          <FieldWrap label="Permitted Users" full>
            <div
              data-testid="app-editor-permitted-users"
              className="max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white p-2"
            >
              {directory.users.map((u) => {
                const checked = form.permitted_emails.includes(u.email);
                return (
                  <label
                    key={u.email}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-slate-50"
                  >
                    <Checkbox
                      data-testid={`app-editor-user-${u.email}`}
                      checked={checked}
                      onCheckedChange={() => toggleEmail(u.email)}
                    />
                    <span className="flex-1 text-sm text-slate-800">
                      {u.full_name || u.email}
                    </span>
                    <span className="text-xs text-slate-500">{u.role}</span>
                  </label>
                );
              })}
            </div>
          </FieldWrap>

          <FieldWrap label="Permitted Roles (optional)" full>
            <div className="flex flex-wrap gap-2 rounded-md border border-slate-200 p-2">
              {directory.roles.map((r) => {
                const checked = form.permitted_roles.includes(r);
                return (
                  <label
                    key={r}
                    className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${
                      checked ? "border-charcoal bg-charcoal text-ink-inverse" : "border-slate-200 bg-slate-50 text-slate-700"
                    }`}
                  >
                    <Checkbox
                      data-testid={`app-editor-role-${r}`}
                      checked={checked}
                      onCheckedChange={() => toggleRole(r)}
                      className="hidden"
                    />
                    {r}
                  </label>
                );
              })}
            </div>
          </FieldWrap>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <X size={14} weight="bold" className="mr-1" /> Cancel
          </Button>
          <Button
            data-testid="app-editor-save"
            onClick={handleSave}
            disabled={saving}
            className="bg-charcoal hover:bg-charcoal"
          >
            <FloppyDisk size={14} weight="bold" className="mr-1" />
            {saving ? "Saving…" : isNew ? "Register" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldWrap({ label, required, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {label}{required && <span className="text-red-500"> *</span>}
      </Label>
      {children}
    </div>
  );
}
